/**
 * Eine aufgezeichnete Aktion zurueckspielen (F-45). Rein und ohne Dexie.
 *
 * Regel: Was nicht zurueck kann, bleibt im Papierkorb. Und: Nichts, was seit
 * der Aktion geaendert wurde, wird stillschweigend zurueckgedreht - je Feld
 * gilt ein Drei-Wege-Abgleich zwischen dem Stand vorher, dem Stand direkt
 * nach der Aktion und dem Stand jetzt.
 */
import { eintragTitel } from '@/domain/sync/typen';
import { gleich, type Daten } from '@/domain/sync/zusammenfuehren';
import { inhaltlichGleich } from '@/domain/papierkorb/verdichten';
import { ANHAENGE, EXKLUSIV, PFLICHT_ELTERN, type Aenderung, type Hinweis } from '@/domain/papierkorb/typen';

const istObjekt = (v: unknown): v is Daten =>
  typeof v === 'object' && v !== null && !Array.isArray(v) && !(v instanceof Uint8Array) && !ArrayBuffer.isView(v);

const elementSchluessel = (x: unknown): string =>
  istObjekt(x) && (typeof x.id === 'string' || typeof x.id === 'number') ? `id:${String(x.id)}` : `w:${JSON.stringify(x)}`;

/**
 * Arrays als Menge zusammenfuehren: Was die Aktion entfernt hat, kommt an
 * seiner alten Stelle wieder hinein (falls es fehlt); was sie hinzugefuegt
 * hat, geht wieder heraus (falls unveraendert); alles seitdem Hinzugekommene
 * bleibt. Objekte mit `id` werden ueber die `id` erkannt, sonst ueber den Wert.
 */
function mengeZusammen(aktuell: unknown[], vorher: unknown[], nachher: unknown[]): unknown[] {
  const vorherNach = new Map(vorher.map((x) => [elementSchluessel(x), x]));
  const nachherNach = new Map(nachher.map((x) => [elementSchluessel(x), x]));
  const ergebnis: unknown[] = [];
  for (const x of aktuell) {
    const k = elementSchluessel(x);
    const v = vorherNach.get(k);
    const n = nachherNach.get(k);
    if (n !== undefined && v === undefined && gleich(x, n)) continue; // von der Aktion hinzugefuegt
    ergebnis.push(v !== undefined && n !== undefined && !gleich(v, n) ? dreiWege(x, v, n) : x);
  }
  const da = () => new Set(ergebnis.map(elementSchluessel));
  let vorhanden = da();
  let pos = 0;
  for (const v of vorher) {
    const k = elementSchluessel(v);
    if (vorhanden.has(k)) {
      pos = ergebnis.findIndex((x) => elementSchluessel(x) === k) + 1;
    } else if (!nachherNach.has(k)) {
      ergebnis.splice(pos, 0, v);
      pos++;
      vorhanden = da();
    }
  }
  return ergebnis;
}

/**
 * Drei-Wege-Abgleich eines Werts. `undefined` heisst: Feld fehlt.
 * - unveraendert seit der Aktion (aktuell = nachher): zurueck auf vorher;
 * - Arrays: als Menge (siehe oben), ein fehlendes Array zaehlt als leer;
 * - einfache Objekte: je Schluessel;
 * - sonst: seither geaendert, der aktuelle Wert bleibt.
 */
export function dreiWege(aktuell: unknown, vorher: unknown, nachher: unknown): unknown {
  if (gleich(aktuell, nachher)) return vorher;
  if (Array.isArray(vorher) || Array.isArray(nachher)) {
    if (aktuell !== undefined && !Array.isArray(aktuell)) return aktuell;
    const v = Array.isArray(vorher) ? vorher : [];
    const n = Array.isArray(nachher) ? nachher : [];
    const r = mengeZusammen((aktuell as unknown[] | undefined) ?? [], v, n);
    return !r.length && vorher === undefined && aktuell === undefined ? undefined : r;
  }
  if (istObjekt(aktuell) && istObjekt(vorher) && istObjekt(nachher)) {
    const r: Daten = { ...aktuell };
    for (const k of new Set([...Object.keys(vorher), ...Object.keys(nachher)])) {
      const w = dreiWege(aktuell[k], vorher[k], nachher[k]);
      if (w === undefined) delete r[k];
      else r[k] = w;
    }
    return r;
  }
  return aktuell;
}

export const schluesselVon = (t: string, id: string) => `${t}/${id}`;

/** Welche Zeilen `planen` lesen muss: die betroffenen und moegliche Pflicht-Eltern. */
export function benoetigteSchluessel(aenderungen: readonly Aenderung[]): { t: string; id: string }[] {
  const out = new Map<string, { t: string; id: string }>();
  const merken = (t: string, id: string) => out.set(schluesselVon(t, id), { t, id });
  for (const a of aenderungen) {
    merken(a.t, a.id);
    const regel = PFLICHT_ELTERN[a.t];
    const eltern = regel && a.vorher && !a.nachher ? a.vorher[regel.feld] : undefined;
    if (typeof eltern === 'string') for (const t of regel!.tabellen) merken(t, eltern);
  }
  return [...out.values()];
}

export interface Plan {
  puts: { t: string; zeile: Daten }[];
  deletes: { t: string; id: string }[];
  /**
   * Angelegte Zeilen, die stehen bleiben, weil sie seitdem geaendert wurden
   * (Hinweis `geaendert`) - ihre Verweise muessen trotzdem stimmen.
   */
  stehen: { t: string; zeile: Daten }[];
  /** Was nicht zurueck kann - bleibt im Papierkorb. */
  bleibt: Aenderung[];
  hinweise: Hinweis[];
  /** Zahl der geschriebenen oder geloeschten Zeilen. */
  zurueck: number;
}

/** Eine geaenderte Zeile per Drei-Wege-Abgleich zurueckfuehren. `null`: nichts zu tun. */
function feldweise(a: Aenderung, aktuell: Daten, jetzt: string): Daten | null {
  const vorher = a.vorher ?? {};
  const nachher = a.nachher ?? {};
  const felder = new Set([...Object.keys(vorher), ...Object.keys(nachher)].filter((k) => k !== 'updatedAt'));
  const gesperrt = new Set<string>();
  for (const gruppe of EXKLUSIV[a.t] ?? []) {
    if (!gruppe.some((f) => felder.has(f))) continue;
    // Haengt die Zeile inzwischen woanders, bleibt die ganze Gruppe, wie sie ist.
    if (gruppe.some((f) => !gleich(aktuell[f], nachher[f]))) for (const f of gruppe) gesperrt.add(f);
  }
  const neu: Daten = { ...aktuell };
  for (const k of felder) {
    if (gesperrt.has(k)) continue;
    const w = dreiWege(aktuell[k], vorher[k], nachher[k]);
    if (w === undefined) delete neu[k];
    else neu[k] = w;
  }
  if (inhaltlichGleich(neu, aktuell)) return null;
  if ('updatedAt' in aktuell || 'updatedAt' in vorher) neu.updatedAt = jetzt;
  return neu;
}

/**
 * Plant das Zurueckspielen.
 * - geloeschte Zeile: fehlt sie, wird sie wieder eingefuegt - ausser der
 *   Schluessel ist neu belegt, ein Pflicht-Elternteil fehlt oder die Bytes
 *   eines Anhangs fehlen; dann bleibt sie im Papierkorb;
 * - geaenderte Zeile: Drei-Wege-Abgleich je Feld; fehlt sie inzwischen
 *   (z. B. Meilenstein entfernt, danach das Projekt geloescht), bleibt sie
 *   mit Grund `fehlt` im Papierkorb - sonst waere die Aenderung still weg;
 * - angelegte Zeile: geloescht, wenn sie seitdem unveraendert ist, sonst
 *   bleibt sie stehen (`stehen`, Hinweis `geaendert`).
 *
 * `lies` liefert den aktuellen Stand (mindestens alles aus
 * `benoetigteSchluessel`), `dateien` die IDs der Anhaenge mit lokalen Bytes.
 */
export function planen(
  aenderungen: readonly Aenderung[],
  lies: (t: string, id: string) => Daten | undefined,
  dateien: ReadonlySet<string>,
  jetzt: string,
): Plan {
  const plan: Plan = { puts: [], deletes: [], stehen: [], bleibt: [], hinweise: [], zurueck: 0 };
  const titel = (a: Aenderung) => eintragTitel(a.vorher ?? a.nachher);
  const bleibt = (a: Aenderung, h: Omit<Hinweis, 't' | 'id' | 'titel'>) => {
    plan.bleibt.push(a);
    plan.hinweise.push({ t: a.t, id: a.id, titel: titel(a), ...h });
  };

  // Geloeschte Zeilen: erst die Kandidaten, dann Pflicht-Eltern bis zum Fixpunkt.
  const kandidaten = new Map<string, Aenderung>();
  for (const a of aenderungen) {
    if (!a.vorher || a.nachher) continue;
    const jetzigeZeile = lies(a.t, a.id);
    if (jetzigeZeile) {
      // Schon wieder da (z. B. per Konflikt uebernommen)? Dann ist nichts zu tun.
      if (!inhaltlichGleich(jetzigeZeile, a.vorher, ['bytes'])) bleibt(a, { grund: 'schon_da' });
      continue;
    }
    if (a.t === ANHAENGE && a.datei && !dateien.has(a.id)) { bleibt(a, { grund: 'datei_fehlt' }); continue; }
    kandidaten.set(schluesselVon(a.t, a.id), a);
  }
  const elternFehlt = (a: Aenderung): { t: string; id: string } | null => {
    const regel = PFLICHT_ELTERN[a.t];
    const id = regel ? a.vorher?.[regel.feld] : undefined;
    if (typeof id !== 'string') return null;
    const da = regel!.tabellen.some((t) => lies(t, id) !== undefined || kandidaten.has(schluesselVon(t, id)));
    return da ? null : { t: regel!.tabellen[0]!, id };
  };
  for (let geaendert = true; geaendert;) {
    geaendert = false;
    for (const [k, a] of kandidaten) {
      const eltern = elternFehlt(a);
      if (!eltern) continue;
      kandidaten.delete(k);
      bleibt(a, { grund: 'eltern_fehlen', eltern });
      geaendert = true;
    }
  }
  for (const a of kandidaten.values()) plan.puts.push({ t: a.t, zeile: a.vorher! });

  for (const a of aenderungen) {
    const aktuell = lies(a.t, a.id);
    if (a.vorher && a.nachher) {
      if (!aktuell) { bleibt(a, { grund: 'fehlt', eltern: { t: a.t, id: a.id } }); continue; }
      const neu = feldweise(a, aktuell, jetzt);
      if (neu) plan.puts.push({ t: a.t, zeile: neu });
    } else if (!a.vorher && a.nachher && aktuell) {
      if (inhaltlichGleich(aktuell, a.nachher, ['bytes'])) {
        plan.deletes.push({ t: a.t, id: a.id });
      } else {
        plan.stehen.push({ t: a.t, zeile: aktuell });
        plan.hinweise.push({ grund: 'geaendert', t: a.t, id: a.id, titel: titel(a) });
      }
    }
  }
  plan.zurueck = plan.puts.length + plan.deletes.length;
  return plan;
}

/** Felder mit einem Verweis auf eine Zeile einer anderen Tabelle. */
const EINZEL_VERWEISE: Readonly<Record<string, string>> = { areaId: 'areas', projectId: 'projects', goalId: 'goals' };
const LISTEN_VERWEISE: Readonly<Record<string, string>> = { areaIds: 'areas', projectIds: 'projects', goalIds: 'goals', taskIds: 'tasks' };
/** Kopplung Aufgabe <-> Zeitblock: nur paarweise gueltig (siehe db/repositories/tasks.ts). */
const KOPPLUNG: Readonly<Record<string, { feld: string; partner: string; rueck: string }>> = {
  tasks: { feld: 'timeBlockEventId', partner: 'events', rueck: 'sourceTaskId' },
  events: { feld: 'sourceTaskId', partner: 'tasks', rueck: 'timeBlockEventId' },
};

/** Welche Zeilen `verweiseBereinigen` nach dem Schreiben lesen muss. */
export function verweisZiele(zeilen: readonly { t: string; zeile: Daten }[]): { t: string; id: string }[] {
  const out = new Map<string, { t: string; id: string }>();
  const merken = (t: string, id: unknown) => { if (typeof id === 'string') out.set(schluesselVon(t, id), { t, id }); };
  for (const { t, zeile } of zeilen) {
    for (const [feld, ziel] of Object.entries(EINZEL_VERWEISE)) merken(ziel, zeile[feld]);
    for (const [feld, ziel] of Object.entries(LISTEN_VERWEISE)) {
      const liste = zeile[feld];
      if (Array.isArray(liste)) for (const id of liste) merken(ziel, id);
    }
    const k = KOPPLUNG[t];
    if (k) merken(k.partner, zeile[k.feld]);
  }
  return [...out.values()];
}

/**
 * Verweise ins Leere in den zurueckgeschriebenen Zeilen bereinigen:
 * Bereich -> `null`, Projekt/Ziel -> entfernt, Listen gefiltert, die
 * Kopplung Aufgabe/Zeitblock nur behalten, wenn beide Seiten zueinander
 * zeigen. `lies` liefert den Stand NACH dem Zurueckschreiben. Gibt nur
 * geaenderte Zeilen zurueck.
 */
export function verweiseBereinigen(
  zeilen: readonly { t: string; zeile: Daten }[],
  lies: (t: string, id: string) => Daten | undefined,
  jetzt: string,
): { t: string; zeile: Daten }[] {
  const geaendert: { t: string; zeile: Daten }[] = [];
  for (const { t, zeile } of zeilen) {
    const neu: Daten = { ...zeile };
    for (const [feld, ziel] of Object.entries(EINZEL_VERWEISE)) {
      const id = neu[feld];
      if (typeof id !== 'string' || lies(ziel, id)) continue;
      if (feld === 'areaId') neu.areaId = null;
      else delete neu[feld];
    }
    for (const [feld, ziel] of Object.entries(LISTEN_VERWEISE)) {
      const liste = neu[feld];
      if (!Array.isArray(liste)) continue;
      const rest = liste.filter((id) => typeof id !== 'string' || lies(ziel, id) !== undefined);
      if (rest.length === liste.length) continue;
      if (!rest.length && feld === 'taskIds') delete neu[feld];
      else neu[feld] = rest;
    }
    const k = KOPPLUNG[t];
    const partnerId = k ? neu[k.feld] : undefined;
    if (k && typeof partnerId === 'string' && lies(k.partner, partnerId)?.[k.rueck] !== neu.id) delete neu[k.feld];
    if (gleich(neu, zeile)) continue;
    if ('updatedAt' in neu) neu.updatedAt = jetzt;
    geaendert.push({ t, zeile: neu });
  }
  return geaendert;
}
